import axios, { type AxiosInstance } from 'axios'
import type { ApiResponse } from '../types'
import { apiError } from './api/client'

// Authentication client. Hits the shared Knowz platform API (VITE_AUTH_URL),
// NOT the Circle of Trust community API. Community calls go through
// `lib/api/client.ts`. Only login / tenant-select / register live here.
const API_BASE = import.meta.env.VITE_API_URL || ''
const AUTH_BASE = import.meta.env.VITE_AUTH_URL || API_BASE

class CotAuthClient {
  private authClient: AxiosInstance
  private jwtToken: string | null = null

  constructor() {
    this.authClient = axios.create({
      baseURL: AUTH_BASE,
      headers: { 'Content-Type': 'application/json' },
    })

    // Carry the JWT on any post-login auth call (e.g. refresh / tenant re-select).
    this.authClient.interceptors.request.use((config) => {
      if (this.jwtToken) {
        config.headers.Authorization = `Bearer ${this.jwtToken}`
      }
      return config
    })
  }

  setToken(token: string) {
    this.jwtToken = token
  }

  clearToken() {
    this.jwtToken = null
  }

  async verifyEmail(token: string): Promise<void> {
    return this.confirmEmail({ token })
  }

  async verifyEmailCode(email: string, code: string): Promise<void> {
    return this.confirmEmail({ email, code })
  }

  private async confirmEmail(request: { token: string } | { email: string; code: string }): Promise<void> {
    const response = await this.authClient.post<ApiResponse>('/api/v1/users/verify-email', request).catch((error: unknown) => { throw apiError(error) })
    if (response.data.success !== true) throw new Error(response.data.errors?.[0] ?? response.data.message ?? 'This email could not be confirmed.')
  }

  async requestPasswordReset(email: string): Promise<void> {
    const response = await this.authClient.post<ApiResponse>('/api/v1/users/forgot-password', { email }).catch((error: unknown) => { throw apiError(error) })
    if (response.data.success === false) throw new Error(response.data.errors?.[0] ?? response.data.message ?? 'Password recovery is unavailable.')
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await this.authClient.post<ApiResponse>('/api/v1/users/reset-password', { token, newPassword }).catch((error: unknown) => { throw apiError(error) })
    if (response.data.success === false) throw new Error(response.data.errors?.[0] ?? response.data.message ?? 'This reset link could not be used.')
  }

  async login(email: string, password: string) {
    const res = await this.authClient.post<ApiResponse<{
      identityId: string
      token: string
      refreshToken?: string
      requiresTenantSelection: boolean
      defaultTenantId?: string
      availableTenants: Array<{
        tenantId: string
        tenantName: string
        userId: string
        role: string
        displayName: string
      }>
    }>>('/api/v1/auth/identity/login', { email, password })

    const data = res.data?.data
    if (!data) {
      throw new Error(res.data?.errors?.[0] || 'Login failed — invalid credentials')
    }

    if (data.token && !data.requiresTenantSelection) {
      const tenant = data.availableTenants?.[0]
      return {
        success: true,
        data: {
          token: data.token,
          tenantId: tenant?.tenantId || data.defaultTenantId || '',
          userId: tenant?.userId || '',
          email,
          displayName: tenant?.displayName || email.split('@')[0],
          role: tenant?.role || 'Owner',
        },
      }
    }

    const tenants = data.availableTenants
    if (!tenants || tenants.length === 0) {
      throw new Error('No tenants found for this account')
    }

    const selectedTenantId = data.defaultTenantId || tenants[0].tenantId
    const tenantRes = await this.authClient.post<ApiResponse<{
      token: string
      refreshToken?: string
      tenantId: string
      userId: string
      email: string
      displayName: string
      role: string
    }>>(`/api/v1/auth/identity/select-tenant/${selectedTenantId}`, {
      identityId: data.identityId,
    })

    const tenantData = tenantRes.data?.data
    if (!tenantData) {
      throw new Error(tenantRes.data?.errors?.[0] || 'Tenant selection failed')
    }

    return {
      success: true,
      data: {
        token: tenantData.token,
        tenantId: tenantData.tenantId,
        userId: tenantData.userId,
        email: tenantData.email || email,
        displayName: tenantData.displayName || email.split('@')[0],
        role: tenantData.role || 'Owner',
      },
    }
  }

  async register(email: string, password: string, displayName: string) {
    const res = await this.authClient.post<ApiResponse<{
      identityId: string
      tenantId: string
      userId: string
    }>>('/api/v1/auth/identity/register', {
      email,
      password,
      displayName,
    })
    return res.data
  }
}

export const apiClient = new CotAuthClient()
